if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.zoomScale = 1;
      this.zoomX = 0;
      this.zoomY = 0;
      this.zoomPointers = new Map();
      this.suppressImageClick = false;
    }

    hide() {
      this.resetZoom();
      if (typeof this.hidePopover === 'function') {
        try {
          if (this.matches(':popover-open')) this.hidePopover();
        } catch (error) {
          this.removeAttribute('popover');
        }
      }
      super.hide();
    }

    show(opener) {
      this.setupZoom();
      // Promote the existing modal above sticky headers and app widgets.
      if (typeof this.showPopover === 'function') {
        this.setAttribute('popover', 'manual');
        try {
          if (!this.matches(':popover-open')) this.showPopover();
        } catch (error) {
          this.removeAttribute('popover'); }
      }
      super.show(opener);
      this.showActiveMedia();
      // Refresh the theme's focus trap after enabling image zoom buttons.
      trapFocus(this, this.querySelector('[role="dialog"]'));
      this.querySelector('[id^="ModalClose-"]')?.focus({ preventScroll: true });
    }

    setupZoom() {
      if (this.zoomControls) return;
      const dialog = this.querySelector('[role="dialog"]');
      if (!dialog) return;

      const controls = document.createElement('div');
      controls.className = 'product-media-modal__zoom-controls';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Image zoom controls');
      controls.innerHTML = `
        <button type="button" class="product-media-modal__zoom-button" data-zoom="out" aria-label="Zoom out">−</button>
        <span class="product-media-modal__zoom-level" aria-live="polite">100%</span>
        <button type="button" class="product-media-modal__zoom-button" data-zoom="in" aria-label="Zoom in">+</button>
        <button type="button" class="product-media-modal__zoom-reset" data-zoom="reset" aria-label="Reset zoom">Reset</button>
      `;
      controls.hidden = true;
      controls.querySelectorAll('button').forEach((button) => { button.disabled = true; });
      controls.addEventListener('pointerup', (event) => event.stopPropagation());
      controls.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-zoom]');
        if (!button || !this.zoomImage) return;
        if (button.dataset.zoom === 'in') this.setZoom(this.zoomScale + 0.5);
        if (button.dataset.zoom === 'out') this.setZoom(this.zoomScale - 0.5);
        if (button.dataset.zoom === 'reset') this.setZoom(1);
      });
      dialog.appendChild(controls);
      this.zoomControls = controls;
      this.querySelector('[id^="ModalClose-"]')?.addEventListener('pointerup', (event) => event.stopPropagation());

      this.querySelectorAll('.product-media-modal__content > img[data-media-id]').forEach((image) => {
        // The theme closes the modal on mouse pointerup. Keep image clicks for zoom.
        image.addEventListener('pointerup', (event) => {
          event.stopPropagation();
          this.endZoomPointer(event);
        });
        image.addEventListener('pointerdown', (event) => this.startZoomPointer(event));
        image.addEventListener('pointermove', (event) => this.moveZoomPointer(event));
        image.addEventListener('pointercancel', (event) => this.endZoomPointer(event));
        image.addEventListener('click', (event) => {
          event.stopPropagation();
          if (this.suppressImageClick) {
            this.suppressImageClick = false;
            return;
          }
          if (this.zoomImage !== image) this.selectZoomImage(image);
          this.setZoom(this.zoomScale === 1 ? 2 : 1);
        });
      });
    }

    selectZoomImage(image) {
      this.resetZoom();
      this.zoomImage = image?.matches('img') ? image : null;
      this.zoomControls.hidden = !this.zoomImage;
      this.zoomControls.querySelectorAll('button').forEach((button) => {
        button.disabled = !this.zoomImage;
      });
      this.updateZoomControls();
    }

    resetZoom() {
      if (this.zoomImage) {
        this.zoomImage.style.transform = '';
        this.zoomImage.classList.remove('is-zoomed');
      }
      this.zoomImage = null;
      this.zoomScale = 1;
      this.zoomX = 0;
      this.zoomY = 0;
      this.zoomPointers.clear();
      this.pinchStartDistance = null;
      this.classList.remove('is-zoomed');
      if (this.zoomControls) this.zoomControls.hidden = true;
      this.updateZoomControls();
    }

    setZoom(scale) {
      this.zoomScale = Math.min(4, Math.max(1, scale));
      if (this.zoomScale === 1) {
        this.zoomX = 0;
        this.zoomY = 0;
      }
      this.renderZoom();
    }

    renderZoom() {
      if (!this.zoomImage) return;
      const maxX = this.zoomImage.offsetWidth * (this.zoomScale - 1) / 2;
      const maxY = this.zoomImage.offsetHeight * (this.zoomScale - 1) / 2;
      this.zoomX = Math.max(-maxX, Math.min(maxX, this.zoomX));
      this.zoomY = Math.max(-maxY, Math.min(maxY, this.zoomY));
      this.zoomImage.style.transform = `translate(${this.zoomX}px, ${this.zoomY}px) scale(${this.zoomScale})`;
      this.zoomImage.classList.toggle('is-zoomed', this.zoomScale > 1);
      this.classList.toggle('is-zoomed', this.zoomScale > 1);
      this.updateZoomControls();
    }

    updateZoomControls() {
      if (!this.zoomControls) return;
      this.zoomControls.querySelector('.product-media-modal__zoom-level').textContent = `${Math.round(this.zoomScale * 100)}%`;
      this.zoomControls.querySelector('[data-zoom="out"]').disabled = !this.zoomImage || this.zoomScale <= 1;
      this.zoomControls.querySelector('[data-zoom="in"]').disabled = !this.zoomImage || this.zoomScale >= 4;
      this.zoomControls.querySelector('[data-zoom="reset"]').disabled = !this.zoomImage || this.zoomScale <= 1;
    }

    startZoomPointer(event) {
      const image = event.currentTarget;
      if (this.zoomImage !== image) this.selectZoomImage(image);
      image.setPointerCapture?.(event.pointerId);
      this.zoomPointers.set(event.pointerId, {
        x: event.clientX, y: event.clientY,
        startX: event.clientX, startY: event.clientY
      });
      if (this.zoomPointers.size === 2) {
        const [first, second] = [...this.zoomPointers.values()];
        this.pinchStartDistance = Math.hypot(first.x - second.x, first.y - second.y);
        this.pinchStartScale = this.zoomScale;
        this.suppressImageClick = true;
      }
    }

    moveZoomPointer(event) {
      const previous = this.zoomPointers.get(event.pointerId);
      if (!previous) return;
      const deltaX = event.clientX - previous.x;
      const deltaY = event.clientY - previous.y;
      this.zoomPointers.set(event.pointerId, { ...previous, x: event.clientX, y: event.clientY });
      if (Math.hypot(event.clientX - previous.startX, event.clientY - previous.startY) > 5)
        this.suppressImageClick = true;
      if (this.zoomPointers.size === 2 && this.pinchStartDistance) {
        const [first, second] = [...this.zoomPointers.values()];
        const distance = Math.hypot(first.x - second.x, first.y - second.y);
        this.setZoom(this.pinchStartScale * distance / this.pinchStartDistance);
      } else if (this.zoomScale > 1) {
        this.zoomX += deltaX;
        this.zoomY += deltaY;
        this.renderZoom();
      }
    }

    endZoomPointer(event) {
      this.zoomPointers.delete(event.pointerId);
      if (this.zoomPointers.size < 2) this.pinchStartDistance = null;
      if (this.suppressImageClick) {
        window.setTimeout(() => { this.suppressImageClick = false; }, 350);
      }
    }

    showActiveMedia() {
      const mediaId = this.openedBy?.getAttribute('data-media-id');
      if (!mediaId) return;
      this.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.toggle('active', element.getAttribute('data-media-id') === mediaId);
      });
      const activeMedia = this.querySelector(`.product-media-modal__content > [data-media-id="${mediaId}"]`);
      if (!activeMedia) return;
      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.scrollIntoView();

      const container = this.querySelector('[role="document"]');
      container.scrollLeft = ((activeMedia.width || 0) - container.clientWidth) / 2;
      this.selectZoomImage(activeMedia);
      if (activeMedia.nodeName === 'DEFERRED-MEDIA' && activeMediaContent?.querySelector('.js-youtube'))
        activeMedia.loadContent();
    }
  });
}
